import {inject, Injectable, signal} from '@angular/core';
import {RecordDataValue, RecordsDataRequestQueryParams} from "../../../types/records-table.types";
import {defaultRecordsDataRequestQueryParams} from "../../../models/default-records-data-request-query-params";
import {UserPreferencesService} from "../../../services/user-preferences.service";
import {dropdownSettingsResources} from "../../../models/resource/tables/dropdown-settings-resource-type";
import {UtilityService} from "../../../services/utility.service";
import {
  ChangeFiltersParams,
  ChangeSortParams,
  PrepareDefaultQueryParams,
  UpdateQueryParams
} from "../types/records-data-request-query-params.types";
import {PageEvent} from "@angular/material/paginator";
import {bloggerModalResources} from "../../../models/resource/tables/blogger-modal-resource-type";
import {productCardModalTableResources} from "../../../models/resource/tables/product-card-resource-types";

@Injectable({
  providedIn: 'root'
})
export class RecordsDataRequestQueryParamsService {
  private userPreferencesService: UserPreferencesService = inject(UserPreferencesService);
  private utilityService: UtilityService = inject(UtilityService);
  private _recordsDataRequestQueryParamsState = signal<RecordsDataRequestQueryParams>(defaultRecordsDataRequestQueryParams);
  public recordsDataRequestQueryParamsState = this._recordsDataRequestQueryParamsState.asReadonly();
  private isProductCardPage = signal<boolean>(false);

  public prepareDefaultQueryParams({ resourceParams, defaultFilters, defaultSort }: PrepareDefaultQueryParams): void {
    this.isProductCardPage.set(Object.keys(productCardModalTableResources).includes(resourceParams.key));
    const userPreferences = this.isProductCardPage() ? {} : this.userPreferencesService.getUserPreferences();
    const defaultSize = this.getDefaultPageSize(resourceParams?.key, userPreferences?.pageSize);
    const isBloggerModal = Object.keys(bloggerModalResources).includes(resourceParams.key);
    const sortParams = isBloggerModal || this.isProductCardPage() ?
      defaultSort :
      userPreferences?.sortParams ?? defaultRecordsDataRequestQueryParams.sort;

    const queryParams: RecordsDataRequestQueryParams = {
      page: defaultRecordsDataRequestQueryParams.page,
      size: defaultSize,
      sort: sortParams,
      ...(isBloggerModal && defaultFilters),
      ...(this.isProductCardPage() && defaultFilters)
    };

    const filtersValues = this.utilityService.removeEmptyProperties(
      userPreferences?.filtersValue ?? defaultFilters ?? {}
    );

    this._recordsDataRequestQueryParamsState.set({
      ...queryParams,
      ...filtersValues
    });
  }

  public updateQueryParams({ updateQueryParamsProps }: UpdateQueryParams): void {
    const { value, key } = updateQueryParamsProps;

    this._recordsDataRequestQueryParamsState.update(currentState => {
      if (value !== undefined && value !== null) {
        return {
          ...currentState,
          [key]: value
        };
      } else {
        const newState = { ...currentState };
        delete newState[key];
        return newState;
      }
    });
  }

  public changeFiltersParams(changeFiltersParams: ChangeFiltersParams): void {
    const { recordsTableFiltersValues, isShowProcessedUserAction } = changeFiltersParams
    const { checkerApproveId } = recordsTableFiltersValues;
    this.updateShowProcessedState(checkerApproveId, isShowProcessedUserAction);
    !this.isProductCardPage() && this.userPreferencesService.saveUserPreferences({ filtersValue: recordsTableFiltersValues });
    const newState = !Object.keys(recordsTableFiltersValues).length
      ? this.utilityService.clearFilters(this.recordsDataRequestQueryParamsState())
      : {
        ...this.recordsDataRequestQueryParamsState(),
        ...recordsTableFiltersValues,
        page: 0
      };

    this._recordsDataRequestQueryParamsState.set(this.utilityService.removeEmptyProperties(newState));
  }

  public changeSortParams({ sortParams }: ChangeSortParams): void {
    const newSort: Array<string> | string = !sortParams ?
      [...defaultRecordsDataRequestQueryParams.sort!] :
      sortParams.includes('id') ?
        sortParams :
        [
          sortParams,
          ...defaultRecordsDataRequestQueryParams.sort!
        ];

    this._recordsDataRequestQueryParamsState.update(currentState => ({
      ...currentState,
      sort: newSort
    }));

    this.userPreferencesService.saveUserPreferences({ sortParams: newSort })
  }

  public changePageParams({ pageIndex, pageSize }: PageEvent): void {
    this._recordsDataRequestQueryParamsState.update(currentState => ({
      ...currentState,
      page: pageIndex,
      size: pageSize
    }));
  }

  public goToPage(selectTablePage: number): void {
    this._recordsDataRequestQueryParamsState.update(currentState => ({
      ...currentState,
      page: selectTablePage - 1
    }));
  }

  private getDefaultPageSize(resourceKey?: string, userPreferenceSize?: number): number {
    if (userPreferenceSize) { return userPreferenceSize; }

    return resourceKey && this.isShouldUpdateDefaultSizeForResourceType(resourceKey) ? 5 : 10;
  }

  private isShouldUpdateDefaultSizeForResourceType(key: string): boolean {
    return key in dropdownSettingsResources;
  }

  private updateShowProcessedState(checkerApproveId: RecordDataValue, isShowProcessedUserAction?: boolean): void {
    if (isShowProcessedUserAction) { return; }

    this._recordsDataRequestQueryParamsState.update(currentState => {
      const newState = { ...currentState };

      if (checkerApproveId) {
        newState.showProcessed = true;
      } else {
        delete newState.showProcessed;
      }

      return newState;
    });
  }
}
